import { useMutation, useQuery } from '@tanstack/react-query';

import { api } from '../lib/api';
import { formatDate } from '../lib/format';

interface BillingState {
  configured: boolean;
  status: string;
  seats: number;
  seatsBilled: number;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  hasPaymentMethod: boolean;
  graceDaysRemaining: number | null;
}

const STATUS_COPY: Record<string, string> = {
  none: 'No subscription yet',
  trialing: 'On trial',
  active: 'Active',
  past_due: 'Payment failed',
  unpaid: 'Payment failed',
  canceled: 'Cancelled',
  incomplete: 'Setup not finished',
  incomplete_expired: 'Setup expired',
  paused: 'Paused',
};

export function Billing({ role }: { role: string }) {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['billing'],
    queryFn: () => api.get<BillingState>('/api/billing'),
  });

  const go = useMutation({
    mutationFn: (path: 'checkout' | 'portal') => api.post<{ url: string }>(`/api/billing/${path}`),
    onSuccess: ({ url }) => {
      // Stripe hosts both flows; a full navigation is correct rather than an iframe.
      window.location.href = url;
    },
  });

  if (isPending) return <p className="muted">Loading…</p>;
  if (error) return <p className="field-error">{(error as Error).message}</p>;
  if (!data) return null;

  const isAdmin = role === 'carrier_admin';

  return (
    <div className="fleet">
      <header className="fleet-head">
        <p className="eyebrow">Subscription</p>
        <h1 className="fleet-title">Billing</h1>
        <p className="ledger">
          You are billed for <span className="ledger-num tone-ok tnum">{data.seats}</span>{' '}
          {data.seats === 1 ? 'driver' : 'drivers'} — everyone on the roster who has not been
          terminated. A former driver's file is retained for three years and is not charged for.
        </p>
      </header>

      {data.graceDaysRemaining !== null && (
        <p className="dd-note" style={{ borderLeftColor: 'var(--st-expired)', background: 'var(--st-expired-bg)' }}>
          <strong>A payment did not go through.</strong> Your records are safe and nothing has been
          deleted. You can keep making changes for{' '}
          <strong>
            {data.graceDaysRemaining} more {data.graceDaysRemaining === 1 ? 'day' : 'days'}
          </strong>
          ; after that the workspace becomes read-only until billing is updated — you will still be
          able to view, download and export everything.
        </p>
      )}

      <section className="card">
        <div className="card-header">
          <h2 className="dd-section-title">{STATUS_COPY[data.status] ?? data.status}</h2>
        </div>
        <div style={{ padding: 'var(--sp-5)', display: 'grid', gap: 'var(--sp-4)' }}>
          <dl className="grid-2" style={{ margin: 0 }}>
            <Fact label="Drivers on the roster" value={String(data.seats)} />
            <Fact
              label="Reported to billing"
              value={
                data.seatsBilled === data.seats
                  ? String(data.seatsBilled)
                  : `${data.seatsBilled} — updating`
              }
            />
            {data.trialEndsAt && (
              <Fact label="Trial ends" value={formatDate(data.trialEndsAt)} />
            )}
            {data.currentPeriodEnd && (
              <Fact
                label={data.cancelAtPeriodEnd ? 'Access ends' : 'Renews'}
                value={formatDate(data.currentPeriodEnd)}
              />
            )}
          </dl>

          {!data.configured ? (
            <p className="muted" style={{ fontSize: 'var(--fs-small)' }}>
              Billing is not switched on for this deployment yet. Nothing is being charged.
            </p>
          ) : !isAdmin ? (
            <p className="muted" style={{ fontSize: 'var(--fs-small)' }}>
              Only a workspace administrator can change billing.
            </p>
          ) : (
            <div className="row">
              {data.hasPaymentMethod ? (
                <button
                  type="button"
                  className="btn"
                  onClick={() => go.mutate('portal')}
                  disabled={go.isPending}
                >
                  {go.isPending ? 'Opening…' : 'Manage billing'}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn"
                  onClick={() => go.mutate('checkout')}
                  disabled={go.isPending}
                >
                  {go.isPending ? 'Opening…' : 'Set up billing'}
                </button>
              )}
              <button type="button" className="btn btn-secondary" onClick={() => void refetch()}>
                Refresh
              </button>
            </div>
          )}

          {go.error && <p className="field-error">{(go.error as Error).message}</p>}
        </div>
      </section>

      <section className="card">
        <div className="card-header">
          <h2 className="dd-section-title">Take your records with you</h2>
        </div>
        <div style={{ padding: 'var(--sp-5)' }}>
          <p className="prose" style={{ maxWidth: '62ch', marginBottom: 'var(--sp-4)' }}>
            Everything in this workspace can be exported at any time, including the audit trail.
            You are required to retain driver qualification files for three years after a driver
            leaves, so leaving should never put you in a difficult position.
          </p>
          {isAdmin ? (
            <a className="btn btn-secondary" href="/api/billing/export">
              Export everything as JSON
            </a>
          ) : (
            <p className="muted" style={{ fontSize: 'var(--fs-small)' }}>
              Ask a workspace administrator for an export.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="eyebrow" style={{ marginBottom: '0.125rem' }}>
        {label}
      </dt>
      <dd className="tnum" style={{ margin: 0, fontSize: 'var(--fs-lg)', fontWeight: 600 }}>
        {value}
      </dd>
    </div>
  );
}
